import {
  filter,
  filterSheetFooterTemplate,
  filterSheetTemplate,
  filterTitles,
  localNavigation,
  menuBarButtonForm,
  selectedNavigationTitle,
} from "../filtersheet/Selectors";
import { setActiveConfiguration, setOpenFilterIds } from "@otto-ec/heureka-components/filter";
import {
  clearEphemeralFirstLevelValues,
  commitEphemeralOrder,
  savePendingEphemeralOrder,
} from "@otto-ec/heureka-components/facet";
import { FilterTitle } from "../filter/FilterTitle";
import { Filter } from "../filter/Filter";
import { FacetValueTag } from "../filter/FacetValueTag";
import { isSelected, SvelteFacetValue } from "../filter/SvelteFacetValue";
import { RefinementBar } from "../refinementBar/RefinementBar";
import type { TrackingLabels } from "../tracking/TrackingLabels";
import { forEachElement, moveAllChildren } from "../util/Utils";
import { FilterSection } from "../filter/FilterSection";
import { FilterTitles } from "../filter/FilterTitles";
import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import { eventQBus } from "../types/EventQBus";
import { registerClickTracker } from "../filtersheet/FilterSheetTracking";
import { readSanPopularFacetValues, updateSanPopularFacetValues } from "../filter/SveltePopularFacetValues";
import PartialLock from "../util/PartialLock";
import { hasChanges } from "../filter/FilterFormActions";
import { readSanTopQuickFacet, updateSanTopQuickFacet } from "../nextBestAction/NextBestActionComponent";
import { OcFilterAccordion } from "../filter/OcFilterAccordion";
import { SortingInput } from "../sorting/SortingInput";
import { exitSecondLevel, isSecondLevelActive } from "./HcAllLevelSheet";
import { markHcSheetClosed, openHcSheet } from "./hcSheetState.svelte";

const FILTER_SHEET_ID = "heureka-hc-filtersheet";
const FILTER_SHEET_TITLE = "Filtern & Sortieren";
const FILTER_SHEET_CONTENT_CLASS = "heureka_filterSheet__content";
const FILTER_SHEET_ACTIONS_CLASS = "heureka_filterSheet__actions";
const FILTER_SHEET_CONTENT_SELECTOR = `.${FILTER_SHEET_CONTENT_CLASS}`;
const FILTER_SHEET_ACTIONS_SELECTOR = `.${FILTER_SHEET_ACTIONS_CLASS}`;
const FILTER_SHEET_LOADING_CLASS = `${FILTER_SHEET_CONTENT_CLASS}--loading`;

export const FIRST_LEVEL_CHANGE_TRACKING_LABELS: Partial<TrackingLabels> = {
  san_FacetLocation: "first_level",
};
export const SIDEBAR_CHANGE_TRACKING_LABELS: Partial<TrackingLabels> = {
  san_FacetLocation: "sidebar",
};
export const SECOND_LEVEL_CHANGE_TRACKING_LABELS: Partial<TrackingLabels> = {
  san_FacetLocation: "second_level",
};
export const NBF_SHEET_CHANGE_TRACKING_LABELS: Partial<TrackingLabels> = {
  san_FacetLocation: "nbf_sheet",
};

export default class HcFilterSheet {
  static readonly factory = HeurekaElementFactory.byId(FILTER_SHEET_ID, HcFilterSheet);

  constructor(
    readonly sheet?: HTMLElement,
    private readonly sheetContent: HTMLElement | null | undefined = sheet?.querySelector(FILTER_SHEET_CONTENT_SELECTOR),
    private readonly sheetActions: HTMLElement | null | undefined = sheet?.querySelector(FILTER_SHEET_ACTIONS_SELECTOR),
  ) {}

  public static pick() {
    return HcFilterSheet.factory.pick();
  }

  public static declareIfOpen(): HcFilterSheet | undefined {
    const sheet = HcFilterSheet.factory.byId(FILTER_SHEET_ID);
    return sheet?.isOpen() ? sheet : undefined;
  }

  public static declare(): HcFilterSheet | undefined {
    const sheet = HcFilterSheet.factory.byId(FILTER_SHEET_ID);
    return sheet?.sheet ? sheet : undefined;
  }

  /**
 *
 */
  public static activeSheetContent(): HTMLElement | null | undefined {
    return HcFilterSheet.declareIfOpen()?.sheetContent;
  }

  private isOpen(): boolean {
    return !!this.sheet?.closest(".hc_sheet--open");
  }

  public static savePopularValuesOrder(event?: Event) {
    if (event) {
      const target = event.target;
      if (!(target instanceof HTMLElement && target.classList.contains("hc_facetValueInput"))) return;
      const scrollBox = target.closest(".hc_facet__scrollBox");
      if (!scrollBox) return;
      const filterId = target.closest("form[id]")?.id?.replace("find_filter_", "");
      if (!filterId) return;

      const values: string[] = [];
      scrollBox.querySelectorAll<HTMLInputElement>(".hc_facetValueInput").forEach((input) => {
        if (input.value) values.push(input.value);
      });
      savePendingEphemeralOrder(filterId, values);
    }
  }

  public static commitPopularValuesOrder() {
    commitEphemeralOrder();
  }

  public static dropPopularValuesOrder() {
    clearEphemeralFirstLevelValues();
  }

  /*       */

  setSheet() {
    const sheetFilterTitles = filterTitles();
    if (sheetFilterTitles) {
      this.sheetContent?.appendChild(sheetFilterTitles);
    }
    return this;
  }

  initializeSheet() {
    const content = this.sheetContent;
    if (content) {
      setOpenFilterIds([]);
      setTimeout(() => {
        setOpenFilterIds(OcFilterAccordion.factory.all().map((a) => a.filterId));
      }, 0);
      setActiveConfiguration("sheetFirstLevel");
      updateSanPopularFacetValues(content, readSanPopularFacetValues());
      updateSanTopQuickFacet(content, readSanTopQuickFacet());

      HcFilterSheet.moveDesktopInlinedFiltersToSheet();

      commitEphemeralOrder();
      const titles = filterTitles();
      if (titles) {
        titles.removeEventListener("change", HcFilterSheet.savePopularValuesOrder, { capture: true });
        titles.addEventListener("change", HcFilterSheet.savePopularValuesOrder, { capture: true });
      }

      menuBarButtonForm()?.addEventListener(
        "click",
        () => {
          FacetValueTag.factory.forEach((tag) => {
            tag.updateTrackingLabelsOnReferenced("deleted", "x_all_local", "first_level");
          });
          SvelteFacetValue.factory
            .all(RefinementBar.facetValueTagsElement())
            .filter(isSelected)
            .forEach((tag) => {
              tag.updateTrackingLabelsOnReferenced("deleted", "x_all_local", "first_level");
            });
        },
        { capture: true },
      );
    }
    return this;
  }

  /*                       */

  deactivate() {
    if (isSecondLevelActive()) {
      exitSecondLevel();
    }

    const source = this.sheetContent;
    if (source) {
      HcFilterSheet.fireDelayedActionsNowOrNever();
      HcFilterSheet.moveInlinedFiltersFrom(source, true, SIDEBAR_CHANGE_TRACKING_LABELS);
      moveAllChildren(source, filterSheetTemplate(), true);
      clearEphemeralFirstLevelValues();
    }

    if (this.sheetActions) {
      moveAllChildren(this.sheetActions, filterSheetFooterTemplate(), true);
    }
  }

  public lockContent() {
    if (this.sheet) {
      this.sheetContent?.classList.add(FILTER_SHEET_LOADING_CLASS);
    }
    return this;
  }

  public unlockContent() {
    this.sheetContent?.classList.remove(FILTER_SHEET_LOADING_CLASS);
    return this;
  }

  public updateContent() {
    if (this.sheetContent) {
      this.sheetContent.innerHTML = "";
    }
    this.setSheet().initializeSheet();
    this.sheetActions?.replaceChildren();
    moveAllChildren(filterSheetFooterTemplate(), this.sheetActions, true);
    return this;
  }

  public removeContent() {
    if (this.sheetContent) {
      this.sheetContent.innerHTML = "";
    }
    this.sheetActions?.replaceChildren();
    return this;
  }

  public close() {
    if (this.sheet) {
      this.deactivate();
      markHcSheetClosed();
      eventQBus.emit("ftfind.filterSheet.closed", {
        filterId: undefined,
        tracking: {},
      });
    }
    return this;
  }

  public open() {
    if (this.sheet) {
      this.setSheet().initializeSheet();
      moveAllChildren(filterSheetFooterTemplate(), this.sheetActions, true);
      SortingInput.resyncToCurrentUrl(this.sheetContent ?? undefined);

      openHcSheet(FILTER_SHEET_TITLE);
      eventQBus.emit("ftfind.filterSheet.opened", { filterId: undefined });
    }
    return this;
  }

  public setScrollOffset(offset?: number) {
    if (typeof offset === "number" && this.sheetContent) {
      this.sheetContent.scrollTop = offset;
    }
    return this;
  }

  /*       */
  static moveDesktopInlinedFiltersToSheet() {
    FilterTitle.factory.forEach((filterTitle) => {
      Filter.updateChangeTrackingLabels(filterTitle.filterId, FIRST_LEVEL_CHANGE_TRACKING_LABELS);
      filterTitle.attachFilter();
    });
  }

  public static scrollToRecentFilter(recentFilterId: string) {
    if (recentFilterId) {
      const content = HcFilterSheet.pick()?.sheetContent;
      const filterSheetFooterRect = HcFilterSheet.pick()?.sheetActions?.getBoundingClientRect();
      const filterTitle = Filter.filterId(recentFilterId)?.filterTitle?.elem;
      const filterTitleRect = filterTitle?.getBoundingClientRect();

      if (
        filterTitleRect &&
        filterSheetFooterRect &&
        content &&
        HcFilterSheet.isHiddenByFooter(filterTitleRect, filterSheetFooterRect) &&
        filterTitle?.scrollIntoView
      ) {
        filterTitle.scrollIntoView(true);
      }
    }
  }

  static isHiddenByFooter(titleRect: DOMRect, footerRect: DOMRect): boolean {
    return !!titleRect && !!footerRect && titleRect.bottom > footerRect.top;
  }

  static moveInlinedFiltersFrom(
    content: HTMLElement,
    updateChangeTracking: boolean,
    trackingLabel: Partial<TrackingLabels>,
  ) {
    FilterTitle.factory.forEach((filterTitle) => {
      if (updateChangeTracking) {
        Filter.updateChangeTrackingLabels(filterTitle.filterId, trackingLabel);
      }
      filterTitle.detachFilter();
    });
  }

  public static addLocalNavigationTitleValue() {
    const title = selectedNavigationTitle(localNavigation());
    const titles = filterTitles();
    if (titles && title) {
      const { overlayEntryTitle = "" } = title.dataset;
      forEachElement(".js_selectedNavigationFilterSheet", (elem) => (elem.innerText = overlayEntryTitle), titles);
    }
  }

  public static addInitialLoadError() {
    const errorMessage = FilterSection.template()?.errorMessage;
    if (errorMessage) {
      FilterTitles.template()?.elem.append(errorMessage.cloneNode(true));
    }
  }

  public addListeners() {
    if (this.sheet) {
      registerClickTracker(this.sheet, {
        capture: true,
        passive: true,
      });
      SortingInput.resyncToCurrentUrl(this.sheetContent ?? undefined);
    }
  }

  public static initPartialLocking() {
    const titles = filterTitles();
    if (titles) {
      PartialLock.create(
        titles,
        ".find_filterTitles__item, .heureka_filterTitle",
        (lockOwner) => {
          const filterId = lockOwner.dataset.filterId;
          const filterForm = filterId && filter(filterId);
          if (filterForm) {
            filterForm.delayedAction?.nowOrNever();
            return hasChanges(filterForm);
          }
          return false;
        },
        {
          unlockTopics: [
            "heureka.filterSection.loadAborted",
            "ftfind.dresonRule.resolveAborted",
            "ftfind.dresonRule.resolved",
          ],
        },
      );
    }
  }

  public static addErrorsOnFilterTitleValues(filterIds?: string[]) {
    const titles = filterTitles();
    if (titles) {
      const invalidIds = filterIds || OcFilterAccordion.invalidFilterIds();

      invalidIds.forEach((filterId) =>
        forEachElement(
          `.find_filterTitles__item[data-filter-id="${filterId}"]`,
          (item) => item.classList.add("find_filterTitles__item--invalid"),
          titles,
        ),
      );
    }
  }

  public static fireDelayedActionsNowOrNever() {
    const titles = filterTitles();
    if (titles) {
      FilterTitle.factory.forEach((filterTitle) => filterTitle.filter?.form?.delayedAction?.nowOrNever());
    }
  }
}
